import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  HomeIcon,
  PhotoIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowUpTrayIcon,
  BellIcon,
  UserCircleIcon,
  SunIcon,
  MoonIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { signOut } from 'aws-amplify/auth';
import { PhotoGrid } from './PhotoGrid';
import PhotoUploader from './PhotoUploader';
import { AggregatedStatisticsView } from './AggregatedStatisticsView';
import StorageCostWidget from './StorageCostWidget';
import { usePhotos, useStats, usePhotoActions, useBursts } from '../hooks/useAPI';
import Settings from '../pages/Settings';
import { Search } from '../pages/Search';
import Account from '../pages/Account';

interface DashboardProps {
  user: {
    name: string;
    email: string;
    avatar_url?: string;
    plan: 'archive' | 'workflow' | 'pro';
  };
}

export const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<'home' | 'photos' | 'bursts' | 'upload' | 'search' | 'stats' | 'settings' | 'account'>('photos');
  const [darkMode, setDarkMode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Fetch real data from API
  const { photos, loading: photosLoading, loadMore, refresh: refreshPhotos } = usePhotos(50);
  const { bursts, loading: burstsLoading, refresh: refreshBursts } = useBursts(50);
  const { stats: apiStats, loading: statsLoading } = useStats('30d');
  const { flagPhoto, cullPhoto, updating } = usePhotoActions();

  const handleSignOut = async () => {
    try {
      console.log('Signing out...');
      await signOut();
      // Clear local storage
      localStorage.clear();
      sessionStorage.clear();
      // Redirect to home page
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      // Force redirect anyway
      window.location.href = '/';
    }
  };

  // Compute stats from API data or use defaults
  const stats = apiStats ? {
    totalPhotos: apiStats.total_photos,
    flaggedForEdit: apiStats.by_status['flagged_for_edit'] || 0,
    uploadedThisWeek: photos.filter(p => {
      const uploadDate = new Date(p.upload_date);
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      return uploadDate > weekAgo;
    }).length,
    storageUsed: '0 GB',
    storagePercent: 0,
  } : {
    totalPhotos: 0,
    flaggedForEdit: 0,
    uploadedThisWeek: 0,
    storageUsed: '0 GB',
    storagePercent: 0,
  };

  const handlePhotoFlag = async (id: string) => {
    await flagPhoto(id);
    refreshPhotos();
  };

  const handlePhotoCull = async (id: string) => {
    await cullPhoto(id);
    refreshPhotos();
  };

  return (
    <div className={`h-screen flex ${darkMode ? 'dark' : ''}`}>
      {/* Sidebar */}
      <motion.aside
        animate={{ width: sidebarCollapsed ? 80 : 256 }}
        className="bg-white dark:bg-neutral-800 border-r border-neutral-200 dark:border-neutral-700 flex flex-col"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-neutral-900 dark:bg-neutral-100 flex items-center justify-center">
              <PhotoIcon className="w-5 h-5 text-white dark:text-neutral-900" />
            </div>
            {!sidebarCollapsed && (
              <span className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                Photosynthesize
              </span>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          <NavItem
            icon={HomeIcon}
            label="Home"
            active={activeTab === 'home'}
            collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('home')}
          />
          <NavItem
            icon={PhotoIcon}
            label="All Photos"
            active={activeTab === 'photos'}
            collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('photos')}
            badge={stats.totalPhotos}
          />
          <NavItem
            icon={ChartBarIcon}
            label="Bursts"
            active={activeTab === 'bursts'}
            collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('bursts')}
            badge={bursts.length}
          />
          <NavItem
            icon={ArrowUpTrayIcon}
            label="Upload"
            active={activeTab === 'upload'}
            collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('upload')}
          />
          <NavItem
            icon={MagnifyingGlassIcon}
            label="Search"
            active={activeTab === 'search'}
            collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('search')}
          />
          <NavItem
            icon={ChartBarIcon}
            label="Statistics"
            active={activeTab === 'stats'}
            collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('stats')}
          />
          <NavItem
            icon={Cog6ToothIcon}
            label="Settings"
            active={activeTab === 'settings'}
            collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('settings')}
          />
          <NavItem
            icon={UserCircleIcon}
            label="Account"
            active={activeTab === 'account'}
            collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('account')}
          />
        </nav>

        {/* Storage Usage */}
        {!sidebarCollapsed && (
          <div className="p-3 border-t border-neutral-200 dark:border-neutral-700">
            <div className="p-3 rounded-lg bg-neutral-50 dark:bg-neutral-900">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
                  Storage Used
                </span>
                <span className="text-xs font-bold text-neutral-900 dark:text-neutral-100">
                  {stats.storageUsed}
                </span>
              </div>
              <div className="h-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                <div
                  style={{ width: `${stats.storagePercent}%` }}
                  className="h-full bg-neutral-900 dark:bg-neutral-100 transition-all duration-500"
                />
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-2">
                Unlimited storage on {user.plan} plan
              </p>
            </div>
          </div>
        )}

        {/* User Profile & Logout */}
        <div className="p-3 border-t border-neutral-200 dark:border-neutral-700 space-y-1">
          {!sidebarCollapsed && (
            <button
              onClick={() => setActiveTab('account')}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
            >
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.name}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <UserCircleIcon className="w-8 h-8 text-neutral-400" />
              )}
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                  {user.email}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                  {user.plan} plan
                </p>
              </div>
            </button>
          )}
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="text-sm font-medium">Sign Out</span>}
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-neutral-50 dark:bg-neutral-900">
        {/* Top Bar */}
        <header className="h-14 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {activeTab === 'home' && 'Home'}
              {activeTab === 'photos' && 'Photos'}
              {activeTab === 'bursts' && 'Bursts'}
              {activeTab === 'upload' && 'Upload'}
              {activeTab === 'search' && 'Search'}
              {activeTab === 'stats' && 'Statistics'}
              {activeTab === 'settings' && 'Settings'}
              {activeTab === 'account' && 'Account'}
            </h1>
            {(photosLoading || statsLoading || burstsLoading) && (
              <div className="w-4 h-4 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Dark Mode Toggle */}
            <button
              onClick={() => {
                console.log('Dark mode toggle clicked, current:', darkMode);
                setDarkMode(!darkMode);
              }}
              className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? (
                <SunIcon className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
              ) : (
                <MoonIcon className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
              )}
            </button>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto">
          {activeTab === 'home' && <HomeView stats={stats} apiStats={apiStats} />}
          {activeTab === 'photos' && (
            <PhotoGrid
              photos={photos}
              onPhotoClick={(photo) => console.log('Open photo:', photo)}
              onPhotoFlag={handlePhotoFlag}
              onPhotoCull={handlePhotoCull}
              onLoadMore={loadMore}
            />
          )}
          {activeTab === 'bursts' && (
            <BurstsView bursts={bursts} loading={burstsLoading} />
          )}
          {activeTab === 'upload' && (
            <div className="p-6 overflow-auto h-full">
              <PhotoUploader onComplete={(count) => {
                console.log(`Uploaded ${count} photos`);
                refreshPhotos();
              }} />
            </div>
          )}
          {activeTab === 'search' && <SearchView />}
          {activeTab === 'stats' && (
            <div className="h-full overflow-auto">
              <AggregatedStatisticsView />
            </div>
          )}
          {activeTab === 'settings' && <SettingsView user={user} />}
          {activeTab === 'account' && <Account />}
        </main>
      </div>
    </div>
  );
};

// NavItem component (unchanged)
interface NavItemProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
  badge?: number;
}

const NavItem: React.FC<NavItemProps> = ({
  icon: Icon,
  label,
  active,
  collapsed,
  onClick,
  badge,
}) => {
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors text-sm
        ${collapsed ? 'justify-center' : ''}
        ${active
          ? 'bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 font-medium'
          : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 hover:text-neutral-900 dark:hover:text-neutral-100'
        }
      `}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      {!collapsed && (
        <>
          <span className="flex-1 text-left">{label}</span>
          {badge !== undefined && badge > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300">
              {badge > 999 ? '999+' : badge}
            </span>
          )}
        </>
      )}
    </button>
  );
};

// HomeView with real stats
const HomeView: React.FC<{ stats: any; apiStats: any }> = ({ stats, apiStats }) => {
  const hasExifData = apiStats && (Object.keys(apiStats.by_camera).length > 0 || Object.keys(apiStats.by_lens).length > 0);

  return (
    <div className="p-6 space-y-6 overflow-auto">
      {/* Welcome Message */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg p-6 border border-neutral-200 dark:border-neutral-700">
        <h2 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Welcome back</h2>
        <p className="text-neutral-600 dark:text-neutral-400">
          {stats.totalPhotos > 0
            ? `You have ${stats.totalPhotos.toLocaleString()} photos in your library${stats.uploadedThisWeek > 0 ? `, ${stats.uploadedThisWeek} uploaded this week` : ''}`
            : 'Upload your first photos to get started'}
        </p>
        {hasExifData && (
          <p className="text-sm text-green-600 dark:text-green-400 mt-2">
            ✓ EXIF data is being processed and analyzed
          </p>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Photos"
          value={stats.totalPhotos.toLocaleString()}
          trend={apiStats?.keeper_rate ? `${Math.round(apiStats.keeper_rate)}% keeper rate` : ''}
          trendUp={false}
        />
        <StatCard
          label="Flagged for Edit"
          value={stats.flaggedForEdit.toLocaleString()}
          trend=""
          trendUp={false}
        />
        <StatCard
          label="Cameras"
          value={apiStats ? Object.keys(apiStats.by_camera).length.toString() : '0'}
          trend={apiStats ? `${Object.keys(apiStats.by_lens).length} lenses` : ''}
          trendUp={false}
        />
        <StatCard
          label="This Week"
          value={stats.uploadedThisWeek.toLocaleString()}
          trend=""
          trendUp={false}
        />
      </div>

      {/* Storage Cost Widget */}
      <StorageCostWidget />

      {/* Camera/Lens Stats */}
      {hasExifData ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
              Top Cameras
            </h3>
            <div className="space-y-3">
              {Object.entries(apiStats.by_camera).slice(0, 5).map(([camera, count]) => (
                <div key={camera} className="flex justify-between items-center">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{camera}</span>
                  <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {count as number} photos
                  </span>
                </div>
              ))}
              {Object.keys(apiStats.by_camera).length === 0 && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">No camera data yet</p>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
              Top Lenses
            </h3>
            <div className="space-y-3">
              {Object.entries(apiStats.by_lens).slice(0, 5).map(([lens, count]) => (
                <div key={lens} className="flex justify-between items-center">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{lens}</span>
                  <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {count as number} photos
                  </span>
                </div>
              ))}
              {Object.keys(apiStats.by_lens).length === 0 && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">No lens data yet</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-neutral-100 dark:bg-neutral-800 rounded-xl p-8 border border-neutral-200 dark:border-neutral-700 text-center">
          <p className="text-neutral-600 dark:text-neutral-400">
            Upload photos to see EXIF statistics (camera, lens, settings, etc.)
          </p>
        </div>
      )}
    </div>
  );
};

// Bursts View
const BurstsView: React.FC<{ bursts: any[]; loading: boolean }> = ({ bursts, loading }) => {
  return (
    <div className="p-6 overflow-auto">
      <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">
        Photo Bursts
      </h2>
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="w-8 h-8 border-4 border-primary-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : bursts.length === 0 ? (
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-12 border border-neutral-200 dark:border-neutral-700 text-center">
          <p className="text-neutral-600 dark:text-neutral-400">
            No bursts detected yet. Upload photos taken in rapid succession to see them grouped here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bursts.map(burst => (
            <div
              key={burst.burst_id}
              className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">
                  {burst.photo_count} photos
                </h3>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  burst.processing_status === 'completed'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
                }`}>
                  {burst.processing_status}
                </span>
              </div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {new Date(burst.start_time).toLocaleString()}
              </p>
              {burst.tier_assignments && (
                <div className="mt-3 flex gap-2 text-xs">
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                    {burst.tier_assignments.hot.length} hot
                  </span>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                    {burst.tier_assignments.warm.length} warm
                  </span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                    {burst.tier_assignments.cold.length} cold
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string;
  trend: string;
  trendUp: boolean;
}> = ({ label, value, trend }) => {
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg p-5 border border-neutral-200 dark:border-neutral-700">
      <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2 uppercase tracking-wide">
        {label}
      </p>
      <div className="flex items-baseline gap-2">
        <p className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
          {value}
        </p>
        {trend && (
          <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
            {trend}
          </span>
        )}
      </div>
    </div>
  );
};

const SearchView: React.FC = () => {
  return <Search />;
};

const SettingsView: React.FC<{ user: any }> = ({ user }) => {
  return <Settings />;
};

export default Dashboard;
